import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, FileText, MessageSquare, UserRound } from "lucide-react";
import { BrokerDealFlowShell } from "../components/BrokerDealFlowShell";
import { BrokerOnly } from "../components/BrokerOnly";
import { dealSubmissionApi } from "../api/dealSubmission";
import type {
  BrokerDealStage,
  PortalDealSubmissionStatusDto,
} from "../api/types";
import {
  PageHeader,
  DetailLayout,
  DetailSection,
  StatusBadge,
  StatusStepper,
  Timestamp,
  EmptyState,
  Skeleton,
  type StatusTone,
} from "@/design-system/components/ui";

// Deal Submission spec §8 — broker drill-in, rebuilt on the shared portal UI
// kit (PageHeader + DetailLayout + StatusBadge + StatusStepper + Timestamp).
// The "Submitted" label is now a high-contrast StatusBadge, the stage rail
// is the shared focal StatusStepper, and history/outstanding/comments read
// as proper sections instead of a sparse left-aligned stack. The payload
// stays the whitelisted PortalDealSubmissionStatusDto — nothing here surfaces
// internal underwriting / criteria / AI / decline-reason data.

const RIBBON: ReadonlyArray<{ stage: BrokerDealStage; label: string; hint: string }> = [
  { stage: "Submitted", label: "Submitted", hint: "We've received your submission." },
  { stage: "DealStaging", label: "Deal Staging", hint: "Your liaison is preparing it for review." },
  { stage: "Intake", label: "Intake", hint: "The deal has entered our pipeline." },
  { stage: "PreApproval", label: "Pre-Approval", hint: "We're confirming it's a fit." },
  { stage: "Underwriting", label: "Underwriting", hint: "The deal is being underwritten." },
  { stage: "Closing", label: "Closing", hint: "We're working toward close." },
  { stage: "Closed", label: "Closed", hint: "This deal has closed." },
];

const STATUS_TONE: Record<PortalDealSubmissionStatusDto["status"], StatusTone> = {
  Draft: "neutral",
  Submitted: "info",
  InStaging: "progress",
  ReturnedForChanges: "warn",
  Approved: "ok",
  Imported: "ok",
  Declined: "danger",
  Withdrawn: "muted",
  // Deal-only rows are non-navigable, so this drill-in never loads one; the
  // key exists only to keep the record exhaustive over DealSubmissionStatus.
  NoSubmission: "ok",
};

const STATUS_HINT: Partial<Record<PortalDealSubmissionStatusDto["status"], string>> = {
  Draft: "You haven't submitted this deal yet.",
  Submitted: "Your liaison will review this shortly.",
  InStaging: "Your liaison is reviewing your submission.",
  ReturnedForChanges: "Please address the comments below and resubmit.",
  Approved: "Approved — moving into our deal pipeline.",
  Declined: "This submission was declined.",
  Withdrawn: "This submission was withdrawn.",
};

export function BrokerDealStatusPage() {
  return (
    <BrokerOnly>
      <StatusBody />
    </BrokerOnly>
  );
}

function StatusBody() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PortalDealSubmissionStatusDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reusable load so resolving a comment can refetch and let the open/
  // outstanding counts tick down in place.
  const load = useCallback(async () => {
    if (!id) return;
    try {
      const dto = await dealSubmissionApi.status(id);
      setData(dto);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load this submission.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <BrokerDealFlowShell pageTitle="Deal status">
        <p className="text-small text-status-error" role="alert">{error}</p>
        <BackLink />
      </BrokerDealFlowShell>
    );
  }
  if (!data) {
    return (
      <BrokerDealFlowShell pageTitle="Deal status">
        <BackLink />
        <div className="mt-space-3 flex flex-col gap-space-3" data-testid="deal-status-loading">
          <Skeleton className="h-9 w-1/2" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </BrokerDealFlowShell>
    );
  }

  const displayName = data.businessName || data.title?.trim() || "Untitled submission";
  const dealNumberLabel = data.dealNumber ? `#${data.dealNumber}` : undefined;
  const isTerminal = data.stage === "Declined" || data.stage === "Withdrawn";
  const activeIdx = RIBBON.findIndex((s) => s.stage === data.stage);

  const editorPath = `/portal/broker/deals/submissions/${data.id}`;
  const openComments = data.returnComments.filter((c) => !c.resolved);
  // The ball is in the broker's court while items are returned or comments
  // are unaddressed (C1 — authoritative "action needed" signal).
  const needsAction = data.status === "ReturnedForChanges" || openComments.length > 0;
  const scrollToComments = () =>
    document.getElementById("reviewer-comments")?.scrollIntoView({ behavior: "smooth" });

  return (
    <BrokerDealFlowShell pageTitle="Deal status">
      <BackLink />
      <DetailLayout
        className="mt-space-3"
        header={
          <PageHeader
            breadcrumbs={[
              { label: "Deals & status", to: "/portal/broker?tab=deals" },
              { label: displayName },
            ]}
            title={displayName}
            subtitle={dealNumberLabel}
            actions={
              <StatusBadge
                tone={STATUS_TONE[data.status]}
                hint={STATUS_HINT[data.status]}
                data-testid="deal-status-label"
              >
                {data.stageLabel}
              </StatusBadge>
            }
          />
        }
        aside={
          <>
            <DetailSection title="What's next">
              {needsAction ? (
                <>
                  <p className="m-0 text-small text-onyx" data-testid="deal-status-next-step">
                    {openComments.length > 0
                      ? `${openComments.length} ${openComments.length === 1 ? "item needs" : "items need"} changes — review the comments and resubmit.`
                      : "You've addressed every comment. Resubmit when you're ready."}
                  </p>
                  {openComments.length > 0 ? (
                    <button
                      type="button"
                      onClick={scrollToComments}
                      className="mt-space-3 inline-block text-small text-gold-shadow underline"
                      data-testid="deal-status-review-comments"
                    >
                      Review the comments →
                    </button>
                  ) : (
                    <Link
                      to={editorPath}
                      className="mt-space-3 inline-block text-small font-semibold text-gold-shadow underline"
                      data-testid="deal-status-resubmit"
                    >
                      Resubmit deal →
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <p className="m-0 text-small text-onyx" data-testid="deal-status-next-step">
                    {data.nextStepText}
                  </p>
                  {data.status === "Draft" && (
                    <Link
                      to={editorPath}
                      className="mt-space-3 inline-block text-small text-gold-shadow underline"
                      data-testid="deal-status-continue"
                    >
                      Continue your submission →
                    </Link>
                  )}
                </>
              )}
            </DetailSection>
            <LiaisonCard name={data.liaisonName} />
          </>
        }
      >
        <DetailSection title="Progress">
          {isTerminal ? (
            <p className="m-0 text-small text-onyx" data-testid="deal-status-terminal">
              This deal was {data.stage === "Declined" ? "declined" : "withdrawn"}.
            </p>
          ) : data.stage === "Draft" ? (
            <p className="m-0 text-small text-gray-mid">
              Your draft hasn't been submitted yet.
            </p>
          ) : (
            <>
              <StatusStepper
                steps={RIBBON.map((s) => ({ key: s.stage, label: s.label, hint: s.hint }))}
                currentIndex={activeIdx < 0 ? 0 : activeIdx}
                complete={data.stage === "Closed"}
                label="Deal stage"
                data-testid="deal-status-ribbon"
              />
              {data.status === "ReturnedForChanges" && (
                <p
                  className="mt-space-3 text-caption text-status-warning"
                  data-testid="deal-status-returned-badge"
                >
                  Returned — see comments below.
                </p>
              )}
            </>
          )}
        </DetailSection>

        {data.outstanding && <OutstandingPanel outstanding={data.outstanding} />}

        {data.returnComments.length > 0 && (
          <ReturnCommentsThread
            comments={data.returnComments}
            submissionId={data.id}
            editorPath={editorPath}
            onResolved={() => void load()}
          />
        )}

        <HistorySection history={data.history} />
      </DetailLayout>
    </BrokerDealFlowShell>
  );
}

function BackLink() {
  return (
    <Link to="/portal/broker?tab=deals" className="text-caption text-onyx underline">
      ← Back to my deals
    </Link>
  );
}

function LiaisonCard({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <DetailSection title="Your liaison" data-testid="deal-status-liaison">
      <div className="flex items-center gap-space-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold-shadow">
          <UserRound size={20} aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="m-0 truncate text-small font-semibold text-onyx" title={name}>
            {name}
          </p>
          <p className="m-0 text-caption text-gray-mid">Here to help with this deal.</p>
        </div>
      </div>
    </DetailSection>
  );
}

function OutstandingPanel({
  outstanding,
}: {
  outstanding: NonNullable<PortalDealSubmissionStatusDto["outstanding"]>;
}) {
  const total =
    outstanding.incompleteRequiredSections +
    outstanding.missingRequiredDocuments +
    outstanding.openReturnComments;
  if (total === 0) {
    return (
      <DetailSection title="Outstanding items" data-testid="deal-status-outstanding-none">
        <div className="flex items-center gap-space-2 text-small text-status-success">
          <CheckCircle2 size={18} aria-hidden />
          No outstanding items — you're all caught up.
        </div>
      </DetailSection>
    );
  }
  return (
    <DetailSection title="Outstanding items" data-testid="deal-status-outstanding">
      <ul className="m-0 flex list-none flex-col gap-space-2 p-0 text-small text-onyx">
        {outstanding.incompleteRequiredSections > 0 && (
          <li className="flex items-center gap-space-2">
            <Clock size={16} className="text-status-warning" aria-hidden />
            {outstanding.incompleteRequiredSections} required section
            {outstanding.incompleteRequiredSections === 1 ? "" : "s"} not yet complete
          </li>
        )}
        {outstanding.missingRequiredDocuments > 0 && (
          <li className="flex items-center gap-space-2">
            <Clock size={16} className="text-status-warning" aria-hidden />
            {outstanding.missingRequiredDocuments} required document
            {outstanding.missingRequiredDocuments === 1 ? "" : "s"} missing
          </li>
        )}
        {outstanding.openReturnComments > 0 && (
          <li className="flex items-center gap-space-2">
            <MessageSquare size={16} className="text-status-warning" aria-hidden />
            {outstanding.openReturnComments} reviewer comment
            {outstanding.openReturnComments === 1 ? "" : "s"} to review
          </li>
        )}
      </ul>
    </DetailSection>
  );
}

type ReturnComment = PortalDealSubmissionStatusDto["returnComments"][number];

function ReturnCommentsThread({
  comments,
  submissionId,
  editorPath,
  onResolved,
}: {
  comments: PortalDealSubmissionStatusDto["returnComments"];
  submissionId: string;
  editorPath: string;
  onResolved: () => void;
}) {
  const open = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);
  return (
    <div id="reviewer-comments">
      <DetailSection
        title="Reviewer comments"
        data-testid="deal-status-comments"
        accessory={
          open.length > 0 ? (
            <span className="text-caption font-semibold text-status-warning" data-testid="deal-status-open-count">
              {open.length} to address
            </span>
          ) : (
            <span className="inline-flex items-center gap-space-1 text-caption text-status-success">
              <CheckCircle2 size={14} aria-hidden /> All addressed
            </span>
          )
        }
      >
        {open.length === 0 && (
          <p className="m-0 text-small text-status-success" data-testid="deal-status-all-addressed">
            You've addressed every reviewer comment — resubmit when you're ready.
          </p>
        )}
        {open.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-space-3 p-0">
            {open.map((c) => (
              <OpenCommentRow
                key={c.id}
                comment={c}
                submissionId={submissionId}
                editorPath={editorPath}
                onResolved={onResolved}
              />
            ))}
          </ul>
        )}
        {resolved.length > 0 && (
          <details className="mt-space-3">
            <summary className="cursor-pointer text-caption text-gray-mid">
              {resolved.length} resolved comment{resolved.length === 1 ? "" : "s"}
            </summary>
            <ul className="m-0 mt-space-2 flex list-none flex-col gap-space-2 p-0">
              {resolved.map((c) => (
                <ResolvedCommentRow key={c.id} comment={c} />
              ))}
            </ul>
          </details>
        )}
      </DetailSection>
    </div>
  );
}

function CommentMeta({ comment }: { comment: ReturnComment }) {
  return (
    <p className="m-0 flex flex-wrap items-center gap-space-2 text-caption text-gray-mid">
      <span className="font-semibold text-onyx">{comment.authorDisplayName ?? "Liaison"}</span>
      <span aria-hidden>·</span>
      <Timestamp value={comment.createdAt} />
      {comment.itemLabel && (
        <span
          className="inline-flex items-center gap-space-1 rounded-pill border border-border-light bg-white-pure px-space-2 py-[1px] font-medium text-onyx"
          data-testid={`deal-status-comment-item-${comment.id}`}
        >
          <FileText size={12} aria-hidden />
          {comment.itemLabel}
        </span>
      )}
    </p>
  );
}

function OpenCommentRow({
  comment,
  submissionId,
  editorPath,
  onResolved,
}: {
  comment: ReturnComment;
  submissionId: string;
  editorPath: string;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function markAddressed() {
    setBusy(true);
    setErr(null);
    try {
      await dealSubmissionApi.resolveReturnComment(submissionId, comment.id);
      onResolved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't update that — try again.");
      setBusy(false);
    }
  }

  return (
    <li
      className="rounded-md border border-status-warning/40 bg-white-warm p-space-3"
      data-testid={`deal-status-comment-${comment.id}`}
    >
      <CommentMeta comment={comment} />
      <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-onyx">{comment.body}</p>
      {err && (
        <p className="m-0 mt-space-1 text-caption text-status-error" role="alert">{err}</p>
      )}
      <div className="mt-space-2 flex flex-wrap items-center gap-space-4">
        <Link
          to={editorPath}
          className="text-caption font-semibold text-gold-shadow underline"
          data-testid={`deal-status-fix-${comment.id}`}
        >
          Fix this →
        </Link>
        <button
          type="button"
          onClick={markAddressed}
          disabled={busy}
          className="text-caption text-onyx underline disabled:text-gray-mid disabled:no-underline"
          data-testid={`deal-status-resolve-${comment.id}`}
        >
          {busy ? "Marking…" : "Mark addressed"}
        </button>
      </div>
    </li>
  );
}

function ResolvedCommentRow({ comment }: { comment: ReturnComment }) {
  return (
    <li
      className="rounded-md border border-border-light bg-white-pure p-space-3 opacity-80"
      data-testid={`deal-status-comment-${comment.id}`}
    >
      <p className="m-0 flex flex-wrap items-center gap-space-2 text-caption text-status-success">
        <CheckCircle2 size={14} aria-hidden /> Addressed
        {comment.resolvedAt && (
          <>
            <span aria-hidden className="text-gray-mid">·</span>
            <Timestamp value={comment.resolvedAt} />
          </>
        )}
        {comment.itemLabel && (
          <span className="rounded-pill border border-border-light bg-white-warm px-space-2 py-[1px] font-medium text-gray-mid">
            {comment.itemLabel}
          </span>
        )}
      </p>
      <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-gray-mid">{comment.body}</p>
    </li>
  );
}

function HistorySection({
  history,
}: {
  history: PortalDealSubmissionStatusDto["history"];
}) {
  if (history.length === 0) {
    return (
      <DetailSection data-testid="deal-status-history">
        <EmptyState
          title="History"
          description="Activity on this deal will show up here as it progresses."
          icon={Clock}
        />
      </DetailSection>
    );
  }
  return (
    <DetailSection title="History" data-testid="deal-status-history">
      <ol className="relative m-0 flex list-none flex-col gap-space-4 p-0 pl-space-4">
        {/* Vertical connector running through the timeline dots. */}
        <span
          aria-hidden
          className="absolute left-[5px] top-space-1 bottom-space-1 w-px bg-border-light"
        />
        {history.map((h, idx) => (
          <li key={idx} className="relative">
            <span
              aria-hidden
              className="absolute -left-space-4 top-[5px] h-[11px] w-[11px] rounded-full border-2 border-gold bg-white-pure"
            />
            <p className="m-0 text-small text-onyx">{h.label}</p>
            <Timestamp value={h.occurredAt} />
          </li>
        ))}
      </ol>
    </DetailSection>
  );
}
